import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IGallery, PRICING_PLAN_TYPE } from "../../../store/services/invitations/dto/invitations.dto";
import { ImageUploaderWrapper } from "./styles";
import { Image, Delete } from "@mui/icons-material";
import {
	Typography,
	Box,
	ImageList,
	ImageListItem,
	Badge,
	IconButton,
	useTheme,
	useMediaQuery,
	Skeleton,
	CircularProgress
} from "@mui/material";
import { useDropzone, Accept } from "react-dropzone";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";
import { v4 as uuidv4, validate } from "uuid";

import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ConfirmModal from "../../Modals/ConfirmModal";
import { toast } from "react-toastify";

import { getVideoDuration } from "../../../utils/getVideoDuration";
import { convertHEICToJPEG } from "../../../utils/convertHeicToJPEG";

const SortableImageItem = ({
	item,
	handleDeleteImage,
	totalItems,
	loadingCount,
	setLoadingCount,
	isOrderDisabled,
	disableDelete
}: {
	item: IGallery;
	handleDeleteImage: (id: string) => void;
	totalItems: number;
	loadingCount: number;
	setLoadingCount: React.Dispatch<React.SetStateAction<number>>;
	isOrderDisabled: boolean;
	disableDelete: boolean;
}) => {
	const videoRef = useRef<HTMLVideoElement>(null);

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: item._id,
		disabled: isOrderDisabled
	});

	useEffect(() => {
		if (!item.video_url || !videoRef.current) return;

		const video = videoRef.current;

		// Force reload
		video.src = item.video_url;
		video.load();

		// Try play/pause trick (iOS hint)
		const tryPlay = async () => {
			try {
				await video.play();
				video.pause();
			} catch {}
		};

		tryPlay();
	}, [item.video_url]);

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
		cursor: "move",
		overflow: "visible"
	};

	// const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {};
	return (
		<ImageListItem
			ref={setNodeRef}
			style={{
				...style,
				touchAction: isDragging ? "none" : "pan-y", // Prevents scroll conflicts
				userSelect: isDragging ? "none" : "auto" // Prevents accidental selection
			}}
			{...attributes}
			{...listeners}
			onTouchStart={e => isDragging && e.stopPropagation()}
		>
			{!isOrderDisabled && <Badge badgeContent={item.order} color="primary" />}
			{item.type === "video" || item.video_url ? (
				<video
					ref={videoRef}
					controls
					playsInline
					muted
					preload="metadata"
					style={{ width: "100%", opacity: loadingCount < totalItems ? 0 : 1 }}
					onLoadedData={() => setLoadingCount(prevState => prevState + 1)}
				>
					{item.video_url && <source src={item.video_url} type="video/mp4" />}
				</video>
			) : (
				<img
					src={item.image_url}
					srcSet={item.image_url}
					alt=""
					draggable={false}
					onContextMenu={e => e.preventDefault()}
					onLoad={() => {
						setLoadingCount(prevState => prevState + 1);
					}}
					style={{ opacity: loadingCount < totalItems ? 0 : 1 }}
				/>
			)}

			<Box className={"image_delete"} style={{ opacity: loadingCount < totalItems ? 0 : 1 }}>
				<IconButton onClick={() => handleDeleteImage(item._id)} disabled={disableDelete}>
					<Delete />
				</IconButton>
			</Box>
		</ImageListItem>
	);
};

const loadingArray = new Array(10).fill(0);
const FileUploaderMultiple = ({
	setFieldValueState,
	fieldValueState,
	deleteImageId,
	setDeleteImageId,
	onDeleteImage,
	isDeletingImage,
	maxFiles: MAX_FILES = 2,
	isOrderDisabled = false,
	disableDelete = false,
	isDisabled,
	setIsDisabled,
	max_image_size = 2,
	max_video_size = 0
}: {
	fieldValueState: IGallery[];
	setFieldValueState: React.Dispatch<React.SetStateAction<IGallery[]>>;
	deleteImageId: string | null;
	setDeleteImageId: React.Dispatch<React.SetStateAction<string | null>>;
	onDeleteImage: (id: string) => void;
	isDeletingImage: boolean;
	isDisabled: boolean;
	setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
	disableDelete?: boolean;
	maxFiles?: number;
	isOrderDisabled?: boolean;
	max_image_size?: number;
	max_video_size?: number;
}) => {
	const MAX_VIDEO_DURATION = 90;
	const MAX_MB = max_image_size;
	const MAX_VIDEO_MB = max_video_size;
	const MAX_FILE_SIZE = MAX_MB * 1024 * 1024;
	const MAX_VIDEO_SIZE = MAX_VIDEO_MB * 1024 * 1024;
	const lastElementRef = useRef<HTMLDivElement>(null);
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const [loadingCount, setLoadingCount] = useState(0);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 10 // Prevent accidental drags
			}
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 250,
				tolerance: 5
			}
		})
	);

	const acceptedFilesTypes = useMemo(() => {
		let _acceptedfiles: Accept = { "image/*": [] };
		if (MAX_VIDEO_SIZE > 0) {
			_acceptedfiles = { ..._acceptedfiles, "video/*": [] };
		}
		return _acceptedfiles;
	}, [MAX_VIDEO_SIZE]);

	const onDrop = useCallback(
		async (acceptedFiles: File[]) => {
			setIsDisabled && setIsDisabled(true);
			const newPreviews: IGallery[] = [];
			const lastElement = fieldValueState[fieldValueState.length - 1];
			let orderBase = lastElement ? lastElement.order : 0;
			for (let index = 0; index < acceptedFiles.length; index++) {
				let file = acceptedFiles[index];
				file = await convertHEICToJPEG(file);
				const isImage = file.type.startsWith("image/");
				const isVideo = file.type.startsWith("video/");
				const order = orderBase + index + 1;
				if (isImage) {
					const img: HTMLImageElement = document.createElement("img");
					img.src = URL.createObjectURL(file);
					img.onload = () => {
						if (img.width < 200 || img.height < 200) {
							notifyError({
								data: { message: "Image must be at least 200x200 pixels." }
							} as IError);
							return;
						}
						const reader = new FileReader();
						reader.onload = () => {
							if (typeof reader.result === "string") {
								newPreviews.push({
									image_url: reader.result,
									_id: uuidv4(),
									order
								} as IGallery);
								if (newPreviews.length === acceptedFiles.length) {
									const newListSorted = newPreviews.sort((a, b) => a.order - b.order);
									setFieldValueState(prev => {
										return [...prev, ...newListSorted].slice(0, MAX_FILES);
									});
								}
							}
						};
						reader.readAsDataURL(file);
					};
					img.onerror = () => {
						notifyError({
							data: { message: "Can not load image" }
						} as IError);
					};
				}
				if (isVideo) {
					try {
						const videoUrl = URL.createObjectURL(file);
						const duration = await getVideoDuration(file);

						if (duration > MAX_VIDEO_DURATION) {
							URL.revokeObjectURL(videoUrl);
							notifyError({
								data: {
									message: `Video must be ${MAX_VIDEO_DURATION} seconds or less.`
								}
							} as IError);
							continue;
						}

						newPreviews.push({
							_id: uuidv4(),
							order,
							image_url: "",
							type: "video",
							video_url: videoUrl
						});
						if (newPreviews.length === acceptedFiles.length) {
							const newListSorted = newPreviews.sort((a, b) => a.order - b.order);
							setFieldValueState(prev => [...prev, ...newListSorted].slice(0, MAX_FILES));
						}
					} catch (e) {
						notifyError({
							data: { message: "Can not read video metadata" }
						} as IError);
					}
				}
			}
			lastElementRef?.current && lastElementRef.current.scrollIntoView({ behavior: "smooth" });
			setIsDisabled && setIsDisabled(false);
		},
		[fieldValueState.length]
	);

	const { getRootProps, getInputProps, fileRejections, acceptedFiles } = useDropzone({
		onDrop,
		accept: acceptedFilesTypes,
		multiple: true,
		validator: function (file) {
			const isImage = file.type.startsWith("image/");
			const isVideo = file.type.startsWith("video/") || file.name.match(/\.(mp4|mov|webm)$/i);
			if (isImage && file.size > MAX_FILE_SIZE) {
				return {
					code: "file-too-large",
					message: `Image exceeds ${MAX_MB}MB`
				};
			}
			if (isVideo && file.size > MAX_VIDEO_SIZE) {
				return {
					code: "file-too-large",
					message: `Video exceeds ${MAX_VIDEO_MB}MB`
				};
			}
			return null;
		}
	});
	const hasNonUploadedImages = useMemo(() => fieldValueState.some(item => validate(item._id)), [fieldValueState]);

	useEffect(() => {
		if (acceptedFiles.length > 0) {
			if (fieldValueState.length + acceptedFiles.length > MAX_FILES) {
				notifyError({
					data: { message: `Files limit exceeded, max (${MAX_FILES}) files are allowed.` }
				} as IError);
			}
		}
	}, [acceptedFiles]);

	useEffect(() => {
		if (fileRejections.length > 0) {
			fileRejections.forEach(item => {
				notifyError({
					data: { message: `${item.file.name} can not be uploaded, ${item.errors.map(err => err.message).join(", ")}` }
				} as IError);
			});
		}
	}, [fileRejections]);

	const handleDragEnd = (event: any) => {
		const { active, over } = event;

		if (active.id !== over?.id) {
			const oldIndex = fieldValueState.findIndex(item => item._id === active.id);
			const newIndex = fieldValueState.findIndex(item => item._id === over?.id);

			const newArray = arrayMove(fieldValueState, oldIndex, newIndex);

			// Optionally reassign 'order' values too
			setFieldValueState(
				newArray.map((item, idx) => ({
					...item,
					order: idx + 1
				}))
			);
		}
	};

	const handleDeleteImageConfirm = (id: string) => {
		setDeleteImageId(id);
	};

	const handleDeleteImage = () => {
		if (validate(deleteImageId)) {
			setFieldValueState(prevState => prevState.filter(item => item._id !== deleteImageId));
			toast.success("Image has been deleted.", { autoClose: 3000 });
			return setDeleteImageId(null);
		}
		if (hasNonUploadedImages) {
			const confirmLeave = window.confirm(
				"You have unsaved images. If you continue, they will be lost. Do you want to proceed?"
			);
			if (!confirmLeave) return;
		}
		onDeleteImage && onDeleteImage(deleteImageId as string);
	};

	return (
		<ImageUploaderWrapper p={2}>
			<Box {...getRootProps()} className="border-dashed border-2 p-8 text-center cursor-pointer">
				<input {...getInputProps()} />
				<Image className={"icon"} />
				<Typography align={"center"}>Drag & drop images here, or click to select</Typography>
				<Typography align={"center"}>
					(Max: {MAX_MB}MB per image{MAX_VIDEO_SIZE > 0 ? ` / ${MAX_VIDEO_MB}MB per video` : ""})
				</Typography>
				{isDisabled && (
					<Box display={"flex"} justifyContent={"center"} width={"100%"}>
						<CircularProgress size={20} />
					</Box>
				)}
			</Box>

			{fieldValueState.length > 0 ? (
				<Box mb={2} mt={1} display={"flex"} justifyContent={"flex-end"}>
					<Typography className={"max_count"}>{"Max uploads: " + fieldValueState.length + "/" + MAX_FILES}</Typography>{" "}
				</Box>
			) : null}

			<Box className="gallery-list" style={{ maxHeight: isSmallerScreen ? "340px" : "500px" }}>
				{loadingCount < fieldValueState.length / 2 && (
					<Box mt={2} display="flex" justifyContent={"center"} style={{ flexFlow: "row wrap" }} gap={2}>
						{loadingArray.map((_, index) => (
							<Box key={index} width={isSmallerScreen ? "100%" : "180px"}>
								<Skeleton variant={"rectangular"} height={"240px"} />
							</Box>
						))}
					</Box>
				)}
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
					onDragStart={e => console.log("Drag started", e)}
				>
					<SortableContext items={fieldValueState.map(item => item._id)} strategy={verticalListSortingStrategy}>
						<ImageList variant="masonry" cols={2} gap={8} sx={{ p: 1 }}>
							{fieldValueState.map(item => (
								<SortableImageItem
									key={item._id}
									item={item}
									handleDeleteImage={handleDeleteImageConfirm}
									totalItems={fieldValueState.length}
									loadingCount={loadingCount}
									setLoadingCount={setLoadingCount}
									isOrderDisabled={isOrderDisabled}
									disableDelete={disableDelete}
								/>
							))}
							<div ref={lastElementRef} />
						</ImageList>
					</SortableContext>
				</DndContext>
			</Box>
			{!!deleteImageId && (
				<ConfirmModal
					open={!!deleteImageId}
					title={"Delete image"}
					message={"Are you sure you want to delete this image?"}
					onConfirm={handleDeleteImage}
					onClose={() => setDeleteImageId(null)}
					confirmText={"Yes, Delete"}
					cancelText={"No, Cancel"}
					loading={isDeletingImage}
				/>
			)}
		</ImageUploaderWrapper>
	);
};

export default FileUploaderMultiple;
